import { useEffect, useState } from "react";
import {
  ApiError,
  buscarCasoParaDuplicado,
  casosPorImei,
  marcarDuplicado,
  type CasoDetalle,
  type CasoDuplicado,
} from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

// Una fila de candidato (sugerido por IMEI o encontrado por folio/TE) con su
// botón para marcarlo como el original. A nivel módulo para no re-montarlo.
function CandidatoDup({
  s,
  marcandoId,
  onMarcar,
}: {
  s: CasoDuplicado;
  marcandoId: number | null;
  onMarcar: (s: CasoDuplicado) => void;
}) {
  return (
    <li className="rounded-md border border-ink-700 bg-ink-800/40 px-3 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-mono text-zinc-200 truncate">{s.folio}</p>
        <p className="text-xs text-zinc-500 truncate">
          {s.cerrado ? `Cerrado hace ${s.dias_desde_cierre} d` : s.estado}
          {s.asignado_nombre ? ` · ${s.asignado_nombre}` : ""}
          {s.imeis_coincidentes.length > 0 ? ` · IMEI ${s.imeis_coincidentes.join(", ")}` : ""}
        </p>
      </div>
      <button
        type="button"
        className="btn bg-accent-600 hover:bg-accent-500 text-white text-xs w-full sm:w-auto flex-shrink-0 disabled:opacity-50"
        disabled={marcandoId !== null}
        onClick={() => onMarcar(s)}
      >
        {marcandoId === s.id ? "Marcando..." : "Es duplicado de este"}
      </button>
    </li>
  );
}

// Modal para marcar el caso actual como duplicado de otro. Sugiere candidatos
// que comparten IMEI y además permite buscar el original a mano por folio o TE
// (para casos sin IMEI o cuando la sugerencia no trae el correcto).
export default function MarcarDuplicadoModal({
  caso,
  onClose,
  onDone,
}: {
  caso: CasoDetalle;
  onClose: () => void;
  onDone: (c: CasoDetalle) => void;
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [sugerencias, setSugerencias] = useState<CasoDuplicado[]>([]);
  const [marcandoId, setMarcandoId] = useState<number | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState<CasoDuplicado | null>(null);
  const [sinResultado, setSinResultado] = useState(false);

  useEffect(() => {
    let activo = true;
    setLoading(true);
    casosPorImei(
      caso.imeis.map((i) => i.imei),
      caso.id,
    )
      .then((r) => {
        if (activo) setSugerencias(r);
      })
      .catch(() => {
        if (activo) setSugerencias([]);
      })
      .finally(() => {
        if (activo) setLoading(false);
      });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  async function marcar(sugerido: CasoDuplicado) {
    setMarcandoId(sugerido.id);
    try {
      const actualizado = await marcarDuplicado(caso.id, sugerido.id);
      toast.success("Marcado como duplicado", `${caso.folio} → ${sugerido.folio}`);
      onDone(actualizado);
      onClose();
    } catch (err) {
      toast.error("No se pudo marcar", err instanceof ApiError ? err.detail : "Error de conexión");
    } finally {
      setMarcandoId(null);
    }
  }

  async function buscar() {
    const q = busqueda.trim();
    if (!q) return;
    setBuscando(true);
    setSinResultado(false);
    setResultado(null);
    try {
      const r = await buscarCasoParaDuplicado(q, caso.id);
      if (r.length > 0) setResultado(r[0]);
      else setSinResultado(true);
    } catch {
      setSinResultado(true);
    } finally {
      setBuscando(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title="Marcar como duplicado"
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          Elige el caso original. <strong className="text-zinc-300">{caso.folio}</strong> quedará
          enlazado como duplicado.
        </p>

        {loading ? (
          <p className="text-sm text-zinc-500">Buscando casos con los mismos IMEIs...</p>
        ) : sugerencias.length === 0 ? (
          <p className="text-sm text-zinc-500">No se encontraron otros casos con el mismo IMEI.</p>
        ) : (
          <ul className="space-y-2">
            {sugerencias.map((s) => (
              <CandidatoDup key={s.id} s={s} marcandoId={marcandoId} onMarcar={marcar} />
            ))}
          </ul>
        )}

        <div className="border-t border-ink-700 pt-3 space-y-2">
          <p className="text-xs text-zinc-500">¿No aparece? Búscalo por folio o TE:</p>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="CASO-2026-00005 o TE-260514-163183"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void buscar();
              }}
            />
            <button
              type="button"
              className="btn bg-ink-700 hover:bg-ink-600 text-zinc-200 border border-ink-600 text-sm disabled:opacity-50"
              disabled={buscando || !busqueda.trim()}
              onClick={() => void buscar()}
            >
              {buscando ? "Buscando..." : "Buscar"}
            </button>
          </div>
          {resultado && (
            <ul className="space-y-2">
              <CandidatoDup s={resultado} marcandoId={marcandoId} onMarcar={marcar} />
            </ul>
          )}
          {sinResultado && (
            <p className="text-xs text-amber-300/80">No hay ningún caso con ese folio o TE.</p>
          )}
        </div>

        <div className="flex justify-end">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}
